import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as api from '../services/api';
import { useAuth } from '../context/AuthContext';
import type { SchoolClass } from '../types/class';
import type {
  RegistrationFieldType,
  RegistrationForm,
  RegistrationFormWorkflowSteps
} from '../types/registration';

const FIELD_TYPES: RegistrationFieldType[] = [
  'text',
  'textarea',
  'number',
  'email',
  'phone',
  'date',
  'select',
  'checkbox'
];

const DEFAULT_WORKFLOW: RegistrationFormWorkflowSteps = {
  submissionReview: true,
  interview: true
};

interface DraftField {
  key: string;
  label: string;
  type: RegistrationFieldType;
  required: boolean;
  optionsText: string;
}

const emptyField = (): DraftField => ({
  key: '',
  label: '',
  type: 'text',
  required: false,
  optionsText: ''
});

function buildCopyTitle(sourceTitle: string): string {
  const prefix = 'Copy of ';
  const maxLength = 200;
  if (prefix.length + sourceTitle.length <= maxLength) {
    return `${prefix}${sourceTitle}`;
  }
  return `${prefix}${sourceTitle.slice(0, maxLength - prefix.length)}`.trimEnd();
}

function fieldsFromForm(form: RegistrationForm): DraftField[] {
  if (form.fields.length === 0) return [emptyField()];
  return [...form.fields]
    .sort((a, b) => a.order - b.order)
    .map((field) => ({
      key: field.key,
      label: field.label,
      type: field.type,
      required: field.required,
      optionsText: field.options?.join(', ') ?? ''
    }));
}

function WorkflowBubbles({
  steps,
  onToggle
}: {
  steps: RegistrationFormWorkflowSteps;
  onToggle: (key: 'submissionReview' | 'interview') => void;
}) {
  return (
    <div className="workflow-bubbles" role="group" aria-label="Registration workflow steps">
      <div className="workflow-bubble-step">
        <span className="workflow-bubble workflow-bubble--locked" aria-hidden="true">
          1
        </span>
        <span className="workflow-bubble-label">Submit</span>
        <span className="workflow-bubble-hint">Always on</span>
      </div>

      <span
        className={
          steps.submissionReview
            ? 'workflow-bubble-connector'
            : 'workflow-bubble-connector workflow-bubble-connector--offline'
        }
        aria-hidden="true"
      />

      <div className="workflow-bubble-step">
        <button
          type="button"
          className={
            steps.submissionReview
              ? 'workflow-bubble workflow-bubble--active'
              : 'workflow-bubble workflow-bubble--offline'
          }
          aria-pressed={steps.submissionReview}
          title={
            steps.submissionReview
              ? 'Review is included — click to skip'
              : 'Review is skipped — click to include'
          }
          onClick={() => onToggle('submissionReview')}
        >
          2
        </button>
        <span className="workflow-bubble-label">Review</span>
        <span className="workflow-bubble-hint">
          {steps.submissionReview ? 'Included' : 'Skipped'}
        </span>
      </div>

      <span
        className={
          steps.interview
            ? 'workflow-bubble-connector'
            : 'workflow-bubble-connector workflow-bubble-connector--offline'
        }
        aria-hidden="true"
      />

      <div className="workflow-bubble-step">
        <button
          type="button"
          className={
            steps.interview
              ? 'workflow-bubble workflow-bubble--active'
              : 'workflow-bubble workflow-bubble--offline'
          }
          aria-pressed={steps.interview}
          title={
            steps.interview
              ? 'Interview is included — click to skip'
              : 'Interview is skipped — click to include'
          }
          onClick={() => onToggle('interview')}
        >
          3
        </button>
        <span className="workflow-bubble-label">Interview</span>
        <span className="workflow-bubble-hint">{steps.interview ? 'Included' : 'Skipped'}</span>
      </div>

      <span className="workflow-bubble-connector" aria-hidden="true" />

      <div className="workflow-bubble-step">
        <span className="workflow-bubble workflow-bubble--locked" aria-hidden="true">
          4
        </span>
        <span className="workflow-bubble-label">Registered</span>
        <span className="workflow-bubble-hint">Always on</span>
      </div>
    </div>
  );
}

export function RegistrationFormEditorPage() {
  const { user } = useAuth();
  const schoolId = user?.schoolId || null;
  const navigate = useNavigate();
  const { formId } = useParams<{ formId: string }>();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copyFrom');

  const isEdit = Boolean(formId);
  const mode: 'blank' | 'copy' | 'edit' = isEdit ? 'edit' : copyFromId ? 'copy' : 'blank';

  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [loading, setLoading] = useState(Boolean(formId || copyFromId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<DraftField[]>([emptyField()]);
  const [workflowSteps, setWorkflowSteps] =
    useState<RegistrationFormWorkflowSteps>(DEFAULT_WORKFLOW);
  const [autoEnrollStudent, setAutoEnrollStudent] = useState(false);
  const [autoEnrollClassId, setAutoEnrollClassId] = useState('');

  const populateFromForm = useCallback((form: RegistrationForm, nextTitle: string) => {
    setTitle(nextTitle);
    setDescription(form.description ?? '');
    setFields(fieldsFromForm(form));
    setWorkflowSteps({ ...form.workflowSteps });
    setAutoEnrollStudent(form.autoEnrollStudent);
    setAutoEnrollClassId(form.autoEnrollClassId ?? '');
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const classItems = await api.listClasses(schoolId!, { activeOnly: true });
        if (!cancelled) setClasses(classItems);

        const sourceId = formId || copyFromId;
        if (sourceId) {
          const forms = await api.listRegistrationForms(schoolId!);
          const form = forms.find((item) => item.id === sourceId);
          if (!form) throw new Error('Registration form not found');
          if (!cancelled) {
            populateFromForm(
              form,
              formId ? form.title : buildCopyTitle(form.title)
            );
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load form editor');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [schoolId, formId, copyFromId, populateFromForm]);

  function updateField(index: number, patch: Partial<DraftField>) {
    setFields((current) =>
      current.map((field, fieldIndex) => (fieldIndex === index ? { ...field, ...patch } : field))
    );
  }

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    if (!schoolId) return;
    if (autoEnrollStudent && !autoEnrollClassId) {
      setError('Select a class when Auto Enroll Student is enabled');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payloadFields = fields
        .filter((field) => field.key.trim() && field.label.trim())
        .map((field, index) => ({
          key: field.key.trim(),
          label: field.label.trim(),
          type: field.type,
          required: field.required,
          options:
            field.type === 'select'
              ? field.optionsText
                  .split(',')
                  .map((option) => option.trim())
                  .filter(Boolean)
              : undefined,
          order: index
        }));

      const payload = {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        fields: payloadFields,
        workflowSteps,
        autoEnrollStudent,
        autoEnrollClassId: autoEnrollStudent ? autoEnrollClassId : null
      };

      if (mode === 'edit' && formId) {
        await api.updateRegistrationForm(schoolId, formId, payload);
      } else {
        await api.createRegistrationForm(schoolId, {
          ...payload,
          description: payload.description || undefined,
          autoEnrollClassId: autoEnrollStudent ? autoEnrollClassId : undefined
        });
      }

      navigate('/registration', { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mode === 'edit'
            ? 'Failed to update registration form'
            : 'Failed to create registration form'
      );
    } finally {
      setSaving(false);
    }
  }

  if (!schoolId) {
    return <Navigate to="/registration" replace />;
  }

  const heading =
    mode === 'edit'
      ? 'Edit registration form'
      : mode === 'copy'
        ? 'Copy registration form'
        : 'Create registration form';

  const subtitle =
    mode === 'edit'
      ? 'Update fields, workflow steps, and auto-enroll settings for this form.'
      : mode === 'copy'
        ? 'Review the copied settings and fields, then save to create a new form.'
        : 'Define fields, optional workflow steps, and auto-enroll settings. Share the public link so applicants can apply.';

  return (
    <section className="admin-panel schools-page">
      <div className="panel-heading">
        <div>
          <h2>{heading}</h2>
          <p>{subtitle}</p>
        </div>
        <Link to="/registration" className="ghost-btn">
          Back to registration
        </Link>
      </div>

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? <p className="muted">Loading form…</p> : null}

      {!loading ? (
        <form className="settings-section register-school-form" onSubmit={handleSave}>
          <div className="settings-section-body">
            <div className="form-grid">
              <label>
                Title
                <input required value={title} onChange={(e) => setTitle(e.target.value)} />
              </label>
              <label>
                Description
                <input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional"
                />
              </label>
            </div>

            <div className="enrollment-block">
              <h4>Workflow steps</h4>
              <p className="muted">
                Click Review or Interview to include or skip that step. Submit and Registered are
                always part of the path.
              </p>
              <WorkflowBubbles
                steps={workflowSteps}
                onToggle={(key) =>
                  setWorkflowSteps((current) => ({
                    ...current,
                    [key]: !current[key]
                  }))
                }
              />
            </div>

            <div className="enrollment-block">
              <h4>Auto enroll</h4>
              <p className="muted">
                When enabled, students who reach Registered are assigned to a class automatically.
              </p>
              <div
                className={
                  autoEnrollStudent ? 'auto-enroll-card auto-enroll-card--on' : 'auto-enroll-card'
                }
              >
                <button
                  type="button"
                  className="auto-enroll-toggle"
                  role="switch"
                  aria-checked={autoEnrollStudent}
                  onClick={() => {
                    const next = !autoEnrollStudent;
                    setAutoEnrollStudent(next);
                    if (!next) setAutoEnrollClassId('');
                  }}
                >
                  <span
                    className={
                      autoEnrollStudent
                        ? 'auto-enroll-switch auto-enroll-switch--on'
                        : 'auto-enroll-switch'
                    }
                    aria-hidden="true"
                  >
                    <span className="auto-enroll-switch-knob" />
                  </span>
                  <span className="auto-enroll-toggle-copy">
                    <strong>Auto Enroll Student</strong>
                    <span>
                      {autoEnrollStudent
                        ? 'Registered applicants will be linked to the class below'
                        : 'Turn on to pick a default class for this form'}
                    </span>
                  </span>
                </button>

                {autoEnrollStudent ? (
                  <div className="auto-enroll-class-panel">
                    <label>
                      Class for this registration form
                      <select
                        required
                        value={autoEnrollClassId}
                        onChange={(e) => setAutoEnrollClassId(e.target.value)}
                      >
                        <option value="">Select a class</option>
                        {classes.map((classDoc) => (
                          <option key={classDoc.id} value={classDoc.id}>
                            {classDoc.name}
                            {classDoc.grade ? ` (${classDoc.grade})` : ''}
                          </option>
                        ))}
                      </select>
                    </label>
                    {classes.length === 0 ? (
                      <p className="muted auto-enroll-empty">
                        No active classes yet. Create a class first.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="enrollment-block">
              <h4>Fields</h4>
              {fields.map((field, index) => (
                <div key={index} className="form-grid registration-field-row">
                  <label>
                    Key
                    <input
                      value={field.key}
                      onChange={(e) => updateField(index, { key: e.target.value })}
                      placeholder="guardianName"
                    />
                  </label>
                  <label>
                    Label
                    <input
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      placeholder="Guardian name"
                    />
                  </label>
                  <label>
                    Type
                    <select
                      value={field.type}
                      onChange={(e) =>
                        updateField(index, {
                          type: e.target.value as RegistrationFieldType
                        })
                      }
                    >
                      {FIELD_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  {field.type === 'select' ? (
                    <label>
                      Options (comma-separated)
                      <input
                        value={field.optionsText}
                        onChange={(e) => updateField(index, { optionsText: e.target.value })}
                        placeholder="Option A, Option B"
                      />
                    </label>
                  ) : null}
                </div>
              ))}
              <div className="form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setFields((current) => [...current, emptyField()])}
                >
                  Add field
                </button>
              </div>
            </div>

            <div className="page-form-actions">
              <button type="submit" className="primary-btn" disabled={saving}>
                {saving
                  ? 'Saving…'
                  : mode === 'edit'
                    ? 'Save changes'
                    : mode === 'copy'
                      ? 'Save copied form'
                      : 'Create form'}
              </button>
              <Link to="/registration" className="ghost-btn">
                Cancel
              </Link>
            </div>
          </div>
        </form>
      ) : null}
    </section>
  );
}
